import { Box, Grid, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { Field, Form, Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { Inputs } from "../../formInputs";
import { useDispatch, useSelector } from "../../../store";
import { getJobTypes } from "../../../slices/jobType";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin, { DateClickArg } from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import JobMilestone from "../../../types/jobMilestone";
import theme from "../../../theme";
import IconSwitcher, { FilledIconName } from "../../base/IconSwitcher";
import moment from "moment";
import { EventClickArg } from "@fullcalendar/core";
import {
  addJobMilestone,
  deleteJobMilestone,
  editJobMilestone,
  getAllJobMilestones,
  getJobMilestone,
  resetJobMilestone,
} from "../../../slices/job";
import {
  deleteMilestone,
  editMilestone,
  getMilestoneId,
  getMilestones,
  postMilestone,
  resetMilestone,
} from "../../../slices/milestones";
import JobMilestoneForm from "../../forms/JobMilestoneForm";
import { Base } from "../../base";
import { setSuccess } from "../../../slices/toast";
import { getHighlights } from "../../../slices/calendarHighlight";

const CalendarView: React.FC = () => {
  const dispatch = useDispatch();
  const { jobTypes } = useSelector((store) => store.jobType);
  const { milestoneList: jobMilestoneList, jobMilestone } = useSelector(
    (store) => store.job,
  );
  const { milestoneList, milestone, status } = useSelector(
    (store) => store.milestone,
  );
  const { highlights } = useSelector((store) => store.highlight);
  const calendarRef = useRef<any>();
  const [milestones, setMilestones] = useState<JobMilestone[]>([]);
  const [milestoneFilter, setMilestoneFilter] = useState<{
    id?: number;
    value?: string;
    color?: string;
  }>();
  const [weekLength, setWeekLength] = useState<number>(4);
  const xs = useMediaQuery(theme.breakpoints.down("sm"));
  const [eventModal, setEventModal] = useState<{
    start?: Date;
    visible: boolean;
  }>({
    start: undefined,
    visible: false,
  });
  const [filterOptions, setFilterOptions] = useState<
    { id?: number; value?: string; color?: string }[]
  >([{ id: -1, value: "User Generated" }]);

  useEffect(() => {
    dispatch(getJobTypes());
    dispatch(getAllJobMilestones());
    dispatch(getMilestones());
    dispatch(getHighlights());
  }, [dispatch]);

  useEffect(() => {
    const list: JobMilestone[] = [...jobMilestoneList, ...milestoneList];
    if (!!milestoneFilter?.id) {
      if (!!milestoneFilter.color) {
        const filterList = list.filter(
          (f) =>
            f.color === milestoneFilter.color ||
            f.calendarHighlight?.color === milestoneFilter.color,
        );
        setMilestones(filterList);
      } else if (milestoneFilter.id > 0) {
        const filterList = list.filter(
          (f) => f.jobTypeId === milestoneFilter.id,
        );
        setMilestones(filterList);
      } else if (milestoneFilter.id === -1) {
        const mainArray = list.map((obj) => {
          if (obj.jobTypeId !== undefined) return obj;
          return { ...obj, jobTypeId: undefined };
        });
        const filterList = mainArray.filter((f) => f.jobTypeId === undefined);
        setMilestones(filterList);
      }
    } else {
      setMilestones(list);
    }
  }, [milestoneList, jobMilestoneList, milestoneFilter, highlights]);

  useEffect(() => {
    if (
      !filterOptions.some((f) =>
        jobTypes.some((j) => j.id === f.id && j.name === f.value),
      ) &&
      !!jobTypes.length
    ) {
      const mappedTypes = jobTypes.map((j) => {
        return { id: j.id, value: j.name };
      });
      setFilterOptions((prev) => [...prev, ...mappedTypes]);
    }
  }, [filterOptions, jobTypes]);

  useEffect(() => {
    if (
      !filterOptions.some((f) =>
        highlights.some((h) => h.id === f.id && h.name === f.value),
      ) &&
      !!highlights.length
    ) {
      const mappedHighlights = highlights.map((h) => {
        return {
          id: h.id,
          value: h.name,
          color: h.color,
        };
      });
      setFilterOptions((prev) => [...prev, ...mappedHighlights]);
    }
  }, [filterOptions, highlights]);

  const renderEventContent = (eventInfo: any) => {
    return (
      <Tooltip title={eventInfo.event.extendedProps.milestoneTitle}>
        <Typography
          variant="h5"
          color={theme.palette.primary.contrastText}
          sx={{
            display: "flex",
            alignItems: "center",
            overflow: "hidden",
            gap: 1,
            p: "3px",
            cursor: status === "loading" ? "progress" : "pointer",
          }}
        >
          <IconSwitcher
            name={
              jobTypes.find(
                (j) => j.id === eventInfo?.event?.extendedProps?.jobTypeId,
              )?.icon
                ? (jobTypes.find(
                    (j) => j.id === eventInfo?.event?.extendedProps?.jobTypeId,
                  )?.icon as FilledIconName)
                : "AccessTime"
            }
            style={{ fontSize: 20 }}
          />
          {xs ? null : eventInfo.event.title}
        </Typography>
      </Tooltip>
    );
  };

  const handleToday = () => {
    let calendarApi = calendarRef.current.getApi();
    calendarApi.today();
    dispatch(getAllJobMilestones());
  };

  const handlePrev = () => {
    let calendarApi = calendarRef.current.getApi();
    calendarApi.prev();
    dispatch(getAllJobMilestones());
  };

  const handleNext = () => {
    let calendarApi = calendarRef.current.getApi();
    calendarApi.next();
    dispatch(getAllJobMilestones());
  };
  const handleTwoWeeks = () => {
    setWeekLength(2);
  };

  const handleThreeWeeks = () => {
    setWeekLength(3);
  };

  const handleFourWeeks = () => {
    setWeekLength(4);
  };

  const handleDateClick = (e: EventClickArg) => {
    if (!!e.event.extendedProps.jobId) {
      if (e.event.extendedProps.answerId) {
        window.open(
          `/job/view/${e.event.extendedProps.jobId}`,
          "_blank",
          "noopener,noreferrer",
        );
      } else {
        dispatch(getJobMilestone(parseInt(e.event.id))).then((result: any) => {
          if (result.meta.requestStatus === "fulfilled") {
            setEventModal({ visible: true });
          }
        });
      }
    } else {
      dispatch(getMilestoneId(parseInt(e.event.id))).then((result: any) => {
        if (result.meta.requestStatus === "fulfilled") {
          setEventModal({ visible: true });
        }
      });
    }
  };

  const handleReset = () => {
    dispatch(resetMilestone());
    dispatch(resetJobMilestone());
    dispatch(getAllJobMilestones());
    dispatch(getMilestones());
  };

  const postSubmit = () => {
    handleReset();
    setEventModal({ visible: false, start: undefined });
  };

  const handleMilestoneSubmit = (values: any) => {
    if (milestone?.id > 0) {
      dispatch(
        editMilestone({
          ...values,
          jobId:
            values.jobType && values.jobType.id ? values.jobType.id : undefined,
          color: values.calendarHighlight.color ?? "",
        }),
      ).then((result) => {
        if (result.meta.requestStatus === "fulfilled") {
          dispatch(setSuccess("Event edited successfully"));
          postSubmit();
        }
      });
    } else if (jobMilestone.id > 0) {
      dispatch(editJobMilestone(values)).then((result) => {
        if (result.meta.requestStatus === "fulfilled") {
          dispatch(setSuccess("Event edited successfully"));
          postSubmit();
        }
      });
    } else if (milestone.id === 0 && values.jobType && values.jobType.id) {
      dispatch(addJobMilestone({ ...values, jobId: values.jobType.id })).then(
        (result) => {
          if (result.meta.requestStatus === "fulfilled") {
            dispatch(setSuccess("Event added successfully"));
            postSubmit();
          }
        },
      );
    } else {
      dispatch(postMilestone(values)).then((result) => {
        if (result.meta.requestStatus === "fulfilled") {
          dispatch(setSuccess("Event added successfully"));
          postSubmit();
        }
      });
    }
  };

  const renderOption = (
    props: React.HtmlHTMLAttributes<HTMLLIElement>,
    option: any,
  ) => {
    return (
      <li
        {...props}
        key={option.value}
        style={{ backgroundColor: theme.palette.background.paper }}
      >
        <Typography
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 1,
          }}
        >
          {option.color && (
            <Box
              sx={{
                height: "25px",
                width: "25px",
                backgroundColor: option.color,
              }}
            />
          )}
          {option.value}
        </Typography>
      </li>
    );
  };

  return (
    <Box
      sx={{
        "& .fc-toolbar-title": {
          fontSize: "30px",
          fontWeight: "500",
        },
        "& .fc-toolbar": {
          display: "flex",
          flexDirection: { sm: "row", xs: "column" },
          gap: 1,
        },
      }}
    >
      <Grid container>
        <Grid size={{ xs: 12, md: 3 }}>
          <Formik
            initialValues={{ type: undefined, calendarHighlight: undefined }}
            onSubmit={() => {}}
          >
            <Form noValidate>
              <Field
                component={Inputs.Dropdown}
                options={filterOptions}
                name="type"
                label="Filter by Type"
                onSelected={(e: React.SyntheticEvent, v: any) => {
                  if (v && v.id) {
                    setMilestoneFilter(v);
                  } else {
                    setMilestoneFilter(undefined);
                  }
                }}
                staticFilter
                renderOption={renderOption}
              />
            </Form>
          </Formik>
        </Grid>
      </Grid>
      <FullCalendar
        ref={calendarRef}
        contentHeight={600}
        plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin, listPlugin]}
        initialView="dayGridMonth"
        weekends
        events={milestones.map((event) => {
          return {
            id: event.id?.toString(),
            start: event.start,
            end: event.end,
            title: event.jobTitle ?? event.title,
            display: "flex",
            color:
              event.color ??
              event.calendarHighlight?.color ??
              jobTypes.find((j) => j.id === event.jobTypeId)?.color ??
              theme.palette.primary.main,
            extendedProps: {
              jobId: event?.jobId,
              jobTypeId: event?.jobTypeId,
              milestoneTitle: event.title,
              answerId: event.answerId,
            },
          };
        })}
        eventContent={renderEventContent}
        dayMaxEventRows
        views={{
          dayGridMonth: {
            duration: { weeks: weekLength },
            dayMaxEventRows: weekLength === 4 ? 3 : weekLength === 3 ? 4 : 5,
          },
        }}
        dateClick={(arg: DateClickArg) => {
          setEventModal({ start: new Date(arg.date), visible: true });
        }}
        dayCellContent={(info) => {
          return moment(info.date).format("MMM DD");
        }}
        headerToolbar={{
          left: xs ? "title" : "twoWeeks threeWeeks fourWeeks",
          center: xs ? "twoWeeks threeWeeks fourWeeks" : "title",
          right: "customToday customPrev,customNext",
        }}
        customButtons={{
          customToday: {
            text: "today",
            click: handleToday,
          },
          customPrev: {
            icon: "chevron-left",
            click: handlePrev,
          },
          customNext: {
            icon: "chevron-right",
            click: handleNext,
          },
          twoWeeks: {
            text: "2 weeks",
            click: handleTwoWeeks,
          },
          threeWeeks: {
            text: "3 weeks",
            click: handleThreeWeeks,
          },
          fourWeeks: {
            text: "4 weeks",
            click: handleFourWeeks,
          },
        }}
        fixedWeekCount={false}
        eventClick={handleDateClick}
      />
      <Base.Modal
        open={eventModal.visible}
        onClose={() => {
          setEventModal({
            start: undefined,
            visible: false,
          });
          dispatch(resetMilestone());
          dispatch(resetJobMilestone());
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            width: xs ? "100%" : 750,
            borderRadius: "10px",
            padding: "15px",
          }}
        >
          <JobMilestoneForm
            start={eventModal.start}
            modal
            handleMilestoneSubmit={handleMilestoneSubmit}
            handleClose={() => {
              setEventModal({ start: undefined, visible: false });
              dispatch(resetMilestone());
              dispatch(resetJobMilestone());
            }}
            handleDelete={() => {
              if (milestone.id > 0) {
                dispatch(deleteMilestone(milestone.id)).then((result) => {
                  if (result.meta.requestStatus === "fulfilled") {
                    dispatch(setSuccess("Event deleted successfully"));
                    handleReset();
                  }
                });
              } else if (jobMilestone.id > 0) {
                dispatch(deleteJobMilestone(jobMilestone.id)).then((result) => {
                  if (result.meta.requestStatus === "fulfilled") {
                    dispatch(setSuccess("Event deleted successfully"));
                    handleReset();
                  }
                });
              }
              setEventModal({ start: undefined, visible: false });
              dispatch(resetMilestone());
            }}
            handleView={
              jobMilestone.jobId
                ? () =>
                    window.open(
                      `/job/view/${jobMilestone.jobId}`,
                      "_blank",
                      "noopener,noreferrer",
                    )
                : undefined
            }
          />
        </Box>
      </Base.Modal>
    </Box>
  );
};

export default CalendarView;
